import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { refreshSession } from '../lib/refreshSession';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import type { AuthUser } from '../types/user.types';
import type { LoginCredentials, LoginOutcome, LoginSuccess } from '../types/auth.types';
import { isTwoFactorChallenge } from '../types/auth.types';
import { loginUser, getCurrentUser, logoutUser, completeTwoFactorLogin } from '../services/auth.service';
import { invalidateCompanyCache } from '../hooks/useCompany';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Performs step 1 of login. Returns either:
   *   - LoginSuccess (token persisted, user state set) — caller should route to dashboard or
   *     to /profile/security/password if `passwordExpired === true`
   *   - TwoFactorChallenge (no token persisted) — caller should route to /login/2fa and
   *     stash the `twoFactorToken` for the step-2 call
   */
  login: (credentials: LoginCredentials) => Promise<LoginOutcome>;
  /** Completes step 2 of a 2FA-challenged login. */
  completeLogin2FA: (twoFactorToken: string, code: string) => Promise<LoginSuccess>;
  logout: () => Promise<void>;
  /** Reload the user. A failure ends the session unless `keepSessionOnError` is set. */
  refreshUser: (options?: { keepSessionOnError?: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // On mount — check for existing session
  useEffect(() => {
    async function initAuth() {
      const token = localStorage.getItem('attendza_token');
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } catch {
        // Token invalid or refresh failed — clear and show login
        localStorage.removeItem('attendza_token');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }
    initAuth();
  }, []);

  // Mirror auth state into Sentry user context so captured exceptions are
  // tagged with the user that hit them. We send id + role ONLY — no email,
  // name, or other PII (we handle UK GDPR-sensitive HR data).
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, role: user.role });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<LoginOutcome> => {
    const outcome = await loginUser(credentials);
    if (isTwoFactorChallenge(outcome)) {
      // Step 1 succeeded but 2FA required — do NOT persist any state yet.
      return outcome;
    }
    localStorage.setItem('attendza_token', outcome.accessToken);
    invalidateCompanyCache();
    setUser(outcome.user);
    return outcome;
  }, []);

  const completeLogin2FA = useCallback(
    async (twoFactorToken: string, code: string): Promise<LoginSuccess> => {
      const outcome = await completeTwoFactorLogin(twoFactorToken, code);
      localStorage.setItem('attendza_token', outcome.accessToken);
      invalidateCompanyCache();
      setUser(outcome.user);
      return outcome;
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutUser();
    } finally {
      localStorage.removeItem('attendza_token');
      invalidateCompanyCache();
      setUser(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const refreshUser = useCallback(
    (options?: { keepSessionOnError?: boolean }): Promise<void> =>
      refreshSession(
        {
          fetchUser: getCurrentUser,
          setUser,
          clearToken: () => localStorage.removeItem('attendza_token'),
        },
        options,
      ),
    [],
  );

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    completeLogin2FA,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
